import { useState } from "react";
import { Stethoscope, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    const em = email.trim().toLowerCase();
    if (!em || !em.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email: em, name: name.trim() });
      setUser(r.data.user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-[#0F172A]" data-testid="login-page">
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1781513144825-aa1e284c5950?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtZWRpY2FsJTIwYWVzdGhldGljcyUyMGNsaW5pYyUyMGludGVyaW9yfGVufDB8fHx8MTc4MzczNjQ1MHww&ixlib=rb-4.1.0&q=85"
          alt="Youngness clinic"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0F172A] via-[#0F172A]/60 to-transparent" />
        <div className="relative z-10 p-14 self-end">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9A24B] mb-3">Youngness Centre</div>
          <h2 className="text-white text-4xl font-light leading-tight" style={{ fontFamily: "Outfit" }}>
            Field Marketing<br />&amp; Partner Relationship CRM
          </h2>
          <p className="text-slate-300 mt-4 max-w-md">
            Every tie-up, every visit, every referral — one source of truth for
            Dr. Awish&apos;s field marketing team.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-[520px] flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-11 h-11 rounded-xl bg-[#C9A24B] flex items-center justify-center">
              <Stethoscope size={22} className="text-[#0F172A]" />
            </div>
            <div>
              <div className="text-white font-semibold text-lg" style={{ fontFamily: "Outfit" }}>
                Youngness Centre
              </div>
              <div className="text-[10px] text-slate-400 tracking-[0.2em] uppercase">by Dr. Awish</div>
            </div>
          </div>
          <h1 className="text-3xl font-light text-white tracking-tight" style={{ fontFamily: "Outfit" }}>
            Welcome back
          </h1>
          <p className="text-slate-400 text-sm mt-2 mb-8">
            Enter your email to continue — no password required.
          </p>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-xs text-slate-400">Email</label>
              <input
                data-testid="login-email"
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[#C9A24B]"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">Full Name <span className="text-slate-500">(optional)</span></label>
              <input
                data-testid="login-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Dr. Awish"
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[#C9A24B]"
              />
            </div>
            <button
              data-testid="btn-login"
              type="submit"
              disabled={loading}
              className="w-full bg-[#C9A24B] hover:bg-[#b08d40] text-[#0F172A] font-semibold rounded-lg py-3 flex items-center justify-center gap-3 transition-colors shadow-lg mt-2"
            >
              {loading && <Loader2 size={16} className="animate-spin" />} Continue
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-800">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Access Control</div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Dr. Awish&apos;s email is auto-recognized as <span className="text-[#C9A24B] font-medium">Founder</span>.
              New team members start as <span className="text-[#C9A24B] font-medium">Manager</span>,
              adjustable to <span className="text-[#C9A24B] font-medium">Executive</span> from the Team page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
